import React from 'react';
import classNames from 'classnames';

import './Slider.scss';

export default function Slider({ min, max, step, infinite, value, onChange, tabIndex=-1, className, ...props }) {
  const onKeyDown = React.useCallback(e => {
    switch (e.key) {
    case "Down":
    case "ArrowDown":
    case "Left":
    case "ArrowLeft":
      if (value > min) {
        onChange(Math.max(value - step, min));
      }
      break;
    case "Up":
    case "ArrowUp":
    case "Right":
    case "ArrowRight":
      if (infinite) {
        onChange(value + step);
      } else if (value < max) {
        onChange(Math.min(value + step, max));
      }
      break;
    case "Home":
      if (value > min) {
        onChange(min);
      }
      break;
    case "End":
      if (value < max) {
        onChange(max);
      }
      break;
    // no default
    }
  }, [min, max, step, infinite, value, onChange]);

  const infA = (Math.E * min - max) / (Math.E - 1);
  const infB = (max - min) / (Math.E - 1);

  const trackRef = React.useRef();
  const pressedRef = React.useRef(null);
  const onPointerMove = React.useCallback(e => {
    if (pressedRef.current == null) return;
    const rect = trackRef.current.getBoundingClientRect();
    let pos = (e.clientX - pressedRef.current - rect.left) / (rect.right - rect.left);
    pos = Math.max(0, Math.min(1, pos));
    let value;
    if (infinite) {
      value = infA + infB / (1 - Math.min(0.99, pos));
    } else {
      value = pos * (max - min) + min;
    }
    if (step && !isNaN(step)) value = Math.round(value / step) * step;
    value = Math.max(value, min);
    if (!infinite) value = Math.min(value, max);
    onChange(value);
  }, [min, max, step, infinite, infA, infB, onChange]);

  const onPointerDown = React.useCallback(e => {
    if (e.target.classList.contains("ui-Slider-thumb")) {
      const rect = e.target.getBoundingClientRect();
      pressedRef.current = e.clientX - (rect.left + rect.right) / 2;
    } else {
      pressedRef.current = 0;
    }
    trackRef.current.setPointerCapture(e.pointerId);
    onPointerMove(e);
  }, [onPointerMove]);

  const onPointerUp = React.useCallback(e => {
    pressedRef.current = null;
    trackRef.current.releasePointerCapture(e.pointerId);
  }, []);

  let pos = Math.max(min, value);
  if (infinite) {
    pos = 1 - infB / (pos - infA);
  } else {
    pos = (pos - min) / (max - min);
  }
  pos = Math.min(1, pos) * 100;

  return (
    <div className={classNames("ui-Slider", className)} tabIndex={tabIndex} {...props} onKeyDown={onKeyDown}>
      <div className="ui-Slider-track" ref={trackRef} onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerMove={onPointerMove}>
        <span className="ui-Slider-thumb" style={{left: `${pos.toFixed(2)}%`}}/>
      </div>
    </div>
  );
}
